import type { LimitFull } from "../shared/limits/limitLines";
import { axiosInstance } from './http'
export { setApiAccessToken, setAuthSdk, setImpersonationAccessToken } from './http'
export { startImpersonation, stopImpersonation } from './sso'

export async function fetchUser() {
  const response = await axiosInstance.get(`/branches`)
  return response.data?.result
}

export async function getBusinessTypeAll() {
  const response = await axiosInstance.get(`/business-type`)
    return response.data
}

export async function createBusinessType({ body }: { body: any }) {
  const response = await axiosInstance.post(`/business-type`, body);
  return response.data;
}

export type BusinessTypeDto = {
  id: string;
  name: string;
  status?: string;
  settings?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string | null;
};

export async function listBusinessTypes() {
  const response = await axiosInstance.get(`/business-type`);
  const data = response.data as BusinessTypeDto[] | { list?: BusinessTypeDto[] };
  if (Array.isArray(data)) {
    return { list: data };
  }
  return { list: data?.list ?? [] };
}

export async function createBusinessTypeAdmin(body: {
  name: string;
  status?: string;
  settings?: Record<string, unknown>;
}) {
  const response = await axiosInstance.post(`/business-type`, body);
  return response.data as BusinessTypeDto;
}

export async function updateBusinessType(id: string, body: {
  name?: string;
  status?: string;
  settings?: Record<string, unknown>;
}) {
  const response = await axiosInstance.patch(`/business-type/${id}`, body);
  return response.data as BusinessTypeDto;
}

export async function deleteBusinessType(id: string) {
  const response = await axiosInstance.delete(`/business-type/${id}`);
  return response.data as { success?: boolean };
}

export async function businessCreate({body}: { body: any }) {
  const response = await axiosInstance.post(`/business`, body)
    return response.data
}

export async function businessDelete(params: { id: string; force?: boolean }) {
  const { id, force } = params;
  const response = await axiosInstance.delete(`/business/${id}`, {
    data: force ? { force: true } : {},
  });
  return response.data;
}

export async function serviceCreate({body}: { body: any }) {
  const config = body instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}
  const response = await axiosInstance.post(`/service`, body, config)
    return response.data
}

export async function updateService({ serviceId, body }: { serviceId: string; body: any }) {
  const config = body instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}
  const response = await axiosInstance.patch(`/service/${serviceId}`, body, config)
    return response.data
}

export async function getServiceAll({body}: { body: any }) {
  const response = await axiosInstance.get(`/service`, body)
    return response.data
}

export async function getServiceById({ serviceId }: { serviceId: string }) {
  const response = await axiosInstance.get(`/service/${serviceId}`)
  return response.data
}

/**
 * Services for a business, used by the resource calendar's "Service" picker.
 * SERVICE_ONLY services have no physical staff/space/resource — their schedule
 * and bookings are keyed by `serviceId` (resourceType=service), so the
 * resource-typed calendar can only reach them through this list.
 */
export async function getServicesByBusiness(businessId: string) {
  const response = await axiosInstance.get(`/service`, {
    params: { businessId, limit: 100 },
  });
  return response.data as {
    items: Array<{
      id: string;
      name: string;
      bookingMode?: string;
      color?: string | null;
    }>;
    total: number;
  };
}

/**
 * Set a resource's calendar overlay colour (7-char hex). Dispatches to the
 * right PATCH endpoint per resource type — staff/space/resource each carry
 * their own `color` column, services already had one.
 */
export async function updateResourceColor(
  resourceType: "staff" | "space" | "resource" | "service",
  id: string,
  color: string,
) {
  switch (resourceType) {
    case "staff":
      return updateStaff({ staffId: id, body: { color } });
    case "space":
      return updateSpace({ id, body: { color } });
    case "service":
      return updateService({ serviceId: id, body: { color } });
    default:
      return updateResource({ id, body: { color } });
  }
}

/** One reason a service can't take online bookings (backend C3 §6). */
export type ReadinessItem = {
  code: string;
  /** `block` stops publishing; `warn` is shown but allowed. */
  severity: 'block' | 'warn';
  message: string;
  /** The edit tab that fixes it. */
  tab: 'basic' | 'rules' | 'who' | 'payment' | 'branches' | 'notify';
  /** Branch names the item is about. */
  detail?: string[];
};

export type ServiceReadiness = { ready: boolean; items: ReadinessItem[] };

/** The plan a service really uses (service row, else business default; price 0 = FREE). */
export type EffectivePlanView = {
  type: 'FREE' | 'BRANCH' | 'FULL' | 'DEPOSIT' | 'INSTALLMENT';
  depositMode?: 'RATE' | 'FIXED';
  /** RATE = percent, FIXED = ₮. */
  depositValue?: number;
  source: 'service' | 'business';
};

/** Price / length per staff member, branch-aware (staff terms). */
export async function getServiceTerms(serviceId: string, branchId?: string | null, addOnIds: string[] = []) {
  const params = new URLSearchParams()
  if (branchId) params.set('branchId', branchId)
  // Picked add-ons: every price / length in the answer is the total.
  if (addOnIds.length) params.set('addOnIds', addOnIds.join(','))
  const q = params.toString() ? `?${params.toString()}` : ''
  const response = await axiosInstance.get(`/service/${serviceId}/terms${q}`)
  return response.data
}

export async function getServiceReadiness(serviceId: string): Promise<ServiceReadiness> {
  const response = await axiosInstance.get(`/service/${serviceId}/readiness`)
  return response.data
}

/** The admin service list: every status, each item with its readiness. */
export async function listServicesWithReadiness(
  businessId: string,
  query: { status?: string; search?: string; categoryId?: string; page?: number; limit?: number },
) {
  const params = new URLSearchParams({ businessId, status: query.status || 'ALL', readiness: '1' })
  if (query.search) params.append('search', query.search)
  if (query.categoryId) params.append('categoryId', query.categoryId)
  if (query.page != null) params.append('page', String(query.page))
  if (query.limit != null) params.append('limit', String(query.limit))
  const response = await axiosInstance.get(`/service?${params.toString()}`)
  return response.data
}

export async function deleteService({ serviceId }: { serviceId: string }) {
  const response = await axiosInstance.delete(`/service/${serviceId}`)
    return response.data
}

export async function getServiceByBusinessId({
  businessId,
  search,
  categoryId,
  status,
  page,
  limit,
}: {
  businessId: string
  search?: string
  categoryId?: string
  status?: string
  page?: number
  limit?: number
}) {
  const params = new URLSearchParams({ businessId })
  if (search) params.append('search', search)
  if (categoryId) params.append('categoryId', categoryId)
  if (status) params.append('status', status)
  if (page != null) params.append('page', String(page))
  if (limit != null) params.append('limit', String(limit))
  const response = await axiosInstance.get(`/service?${params.toString()}`)
  // Backend now returns { items, total, page, limit, hasMore }; old callers
  // that just want the array can use response.data.items. We return the full
  // envelope so paginated callers can read total/hasMore too.
  return response.data
}

export async function getStaffAll() {
  const response = await axiosInstance.get(`/staff`)
    return response.data
}


export async function getBranchByBusinessId({ businessId }: { businessId: string }) {
  const response = await axiosInstance.get(`/branch?businessId=${businessId}`)
    return response.data
} 

export async function getBranchId({ branchId }: { branchId: string }) {
  const response = await axiosInstance.get(`/branch/${branchId}`)
    return response.data
}

// ─── Branch staff M:N (Phase D — branch detail Staff tab) ───

/** List staff assigned to this branch via the staff_branches M:N. Only
 *  returns ACTIVE staff. Mirrors GET /branch/:id/staff. */
export async function listBranchStaff({
  branchId,
}: {
  branchId: string;
}): Promise<
  Array<{
    id: string;
    firstName?: string | null;
    lastName: string;
    email?: string | null;
    phone?: string | null;
  }>
> {
  const response = await axiosInstance.get(`/branch/${branchId}/staff`);
  return response.data;
}

/** Idempotent assign staff to branch's roster. */
export async function addStaffToBranch({
  branchId,
  staffId,
}: {
  branchId: string;
  staffId: string;
}): Promise<{ ok: true }> {
  const response = await axiosInstance.post(
    `/branch/${branchId}/staff/${staffId}`,
    {},
  );
  return response.data;
}

/** Idempotent unassign staff from branch's roster. Does not delete staff. */
export async function removeStaffFromBranch({
  branchId,
  staffId,
}: {
  branchId: string;
  staffId: string;
}): Promise<{ ok: true }> {
  const response = await axiosInstance.delete(
    `/branch/${branchId}/staff/${staffId}`,
  );
  return response.data;
}


/**
 * Fetch staff for a business. `status` controls which lifecycle bucket is
 * returned: ACTIVE (default — the working list), DELETED (archived view
 * for restore), INACTIVE (publish-gated). Backend defaults to ACTIVE when
 * the param is omitted; we always pass it explicitly so the URL is
 * self-describing in the network panel.
 */
export async function getStaffByBusinessId({
  businessId,
  status,
}: {
  businessId: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'DELETED' | 'SUSPENDED';
}) {
  const params = new URLSearchParams({ businessId });
  if (status) params.set('status', status);
  const response = await axiosInstance.get(`/staff?${params.toString()}`);
  return response.data;
}

export async function staffCreate({body}: { body: any }) {
  console.log("🚀 ~ staffCreate ~ body:", body)
  const config = body instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}
  const response = await axiosInstance.post(`/staff`, body, config)
    return response.data
}

export async function updateStaff({ staffId, body }: { staffId: string; body: any }) {
  const config = body instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}
  const response = await axiosInstance.patch(`/staff/${staffId}`, body, config)
    return response.data
}

export async function deleteStaff({ staffId }: { staffId: string }) {
  const response = await axiosInstance.delete(`/staff/${staffId}`)
    return response.data
}

/**
 * Inverse of deleteStaff. Backend `remove()` is a soft delete that sets
 * status=DELETED; restoring is a PATCH that sets status back to ACTIVE.
 * The staff's previous data (name, email, ssoUserId link if any) is
 * untouched by either operation.
 */
export async function restoreStaff({ staffId }: { staffId: string }) {
  const response = await axiosInstance.patch(`/staff/${staffId}`, { status: 'ACTIVE' });
  return response.data;
}

export async function inviteStaffOperator({
  staffId,
  ssoUserId,
  roleIds,
}: {
  staffId: string;
  ssoUserId: string;
  roleIds?: string[];
}) {
  const response = await axiosInstance.post(`/staff/${staffId}/invite-operator`, {
    ssoUserId,
    roleIds,
  });
  return response.data as { staff: any; membership: any };
}

export async function removeStaffOperatorLink({ staffId }: { staffId: string }) {
  const response = await axiosInstance.delete(`/staff/${staffId}/operator-link`);
  return response.data as { id: string; ssoUserId: string | null };
}

/**
 * Phase 4: email-based invitation. Mints a chatbot_sso invitation in the
 * backend and emails the recipient a magic link. The raw token never
 * comes back here — it's embedded in the email by the backend.
 */
export async function inviteStaffByEmail({
  staffId,
  email,
  roleIds,
}: {
  staffId: string;
  email: string;
  roleIds?: string[];
}) {
  const response = await axiosInstance.post(`/staff/${staffId}/invite-by-email`, {
    email,
    ...(roleIds?.length ? { roleIds } : {}),
  });
  return response.data as {
    invitationId: string;
    email: string;
    expiresAt: string;
  };
}

/**
 * Accept callback — consumed by the /staff/invitations/accept admin page
 * after the recipient bounces back from chatbot_sso. The acceptance JWT is
 * the credential; backend validates and finalises membership + staff link.
 */
export async function acceptStaffInvitation({ acceptance }: { acceptance: string }) {
  const response = await axiosInstance.post(`/staff/invitations/accept`, { acceptance });
  return response.data as {
    ok: true;
    staffId: string;
    ssoUserId: string;
    membershipId: string;
    businessId: string;
    invitationJti: string;
  };
}

export type StaffInvitationRow = {
  id: string;
  email: string;
  sourceApp: string;
  metadata: { businessId?: string; staffId?: string; roleIds?: string[] } & Record<string, unknown>;
  expiresAt: string;
  createdAt: string;
};

/** List pending invitations scoped to a business (and optionally one staff). */
export async function listStaffInvitations(params: {
  businessId: string;
  staffId?: string;
}): Promise<StaffInvitationRow[]> {
  const response = await axiosInstance.get(`/staff/invitations`, {
    params: {
      businessId: params.businessId,
      ...(params.staffId ? { staffId: params.staffId } : {}),
    },
  });
  return Array.isArray(response.data) ? (response.data as StaffInvitationRow[]) : [];
}

export async function revokeStaffInvitation({ invitationId }: { invitationId: string }) {
  const response = await axiosInstance.post(`/staff/invitations/${invitationId}/revoke`);
  return response.data as { ok: true };
}

export async function resendStaffInvitation({ invitationId }: { invitationId: string }) {
  const response = await axiosInstance.post(`/staff/invitations/${invitationId}/resend`);
  return response.data as {
    invitationId: string;
    email: string;
    expiresAt: string;
    previouslyRevoked: string;
  };
}

export async function deleteSpaces({ spaceId }: { spaceId: string }) {
  const response = await axiosInstance.delete(`/space/${spaceId}`)
    return response.data
}

export async function deleteResource({ resourceId }: { resourceId: string }) {
  const response = await axiosInstance.delete(`/resource/${resourceId}`)
    return response.data
}

export async function resourceCreate({body}: { body: any }) {
  console.log("🚀 ~ resourceCreate ~ body:", body)
  const response = await axiosInstance.post(`/resource`, body)
    console.log("🚀 ~ resourceCreate ~ response:", response)
    return response.data
}

export async function updateResource({ id, body }: { id: string; body: any }) {
  const response = await axiosInstance.patch(`/resource/${id}`, body);
  return response.data;
}

export async function getResource({businessId}: { businessId: string }) {
  const response = await axiosInstance.get(`/resource?businessId=${businessId}`)
    return response.data
}

export async function createSpace({ body }: { body: any }) {
  const response = await axiosInstance.post(`/space`, body)
  return response.data 
}

export async function updateSpace({ id, body }: { id: string; body: any }) {
  const response = await axiosInstance.patch(`/space/${id}`, body);
  return response.data;
}

/** Single-record loads for cold entry into the staff/space/resource edit pages. */
export async function getStaffById(id: string) {
  const response = await axiosInstance.get(`/staff/${id}`);
  return response.data;
}

export async function getSpaceById(id: string) {
  const response = await axiosInstance.get(`/space/${id}`);
  return response.data;
}

export async function getResourceById(id: string) {
  const response = await axiosInstance.get(`/resource/${id}`);
  return response.data;
}

export async function getSpace({ businessId }: { businessId?: string }) {
  const response = await axiosInstance.get(`/space?businessId=${businessId}`);
  return response.data;
}

// Accepts spaceId, resourceId, staffId, serviceId as query (matches backend GET /schedule)
export async function getSchedule(query: {
  spaceId?: string;
  resourceId?: string;
  staffId?: string;
  serviceId?: string;
  /** Phase D — branch venue hours (Layer 1). Polymorphic on the backend
   *  side; no Schedule FK relation for branch. */
  branchId?: string;
}) {
  const params = new URLSearchParams();
  if (query.spaceId) params.append('spaceId', query.spaceId);
  if (query.resourceId) params.append('resourceId', query.resourceId);
  if (query.staffId) params.append('staffId', query.staffId);
  if (query.serviceId) params.append('serviceId', query.serviceId);
  if (query.branchId) {
    // Branch scope uses the polymorphic resourceType discriminator;
    // backend's findAll routes on (resourceType=branch, resourceId).
    params.append('resourceType', 'branch');
    params.append('resourceId', query.branchId);
  }
  const response = await axiosInstance.get(`/schedule?${params.toString()}`);
  return response.data;
}

export async function createSchedule({ body }: { body: any }) {
  const response = await axiosInstance.post(`/schedule`, body)
  return response.data 
}

export async function updateSchedule({ id, body }: { id: string; body: any }) {
  const response = await axiosInstance.patch(`/schedule/${id}`, body)
  return response.data 
}

export async function deleteAvailability({ id }: { id: string }) {
  const response = await axiosInstance.delete(`/availability/${id}`);
  return response.data;
}

export async function createAvailability({ body }: { body: any }) {
  const response = await axiosInstance.post(`/availability`, body);
  return response.data;
}

export async function updateAvailability({ id, body }: { id: string; body: any }) {
  const response = await axiosInstance.patch(`/availability/${id}`, body);
  return response.data;
}

export async function getAvailabilityList({ query }: { query: any }) {
  const response = await axiosInstance.get(`/availability?${new URLSearchParams(query).toString()}`);
  return response.data;
}

export async function getScheduleCalendar({ query }: { query: any }) {
  const response = await axiosInstance.get(`/schedule/calendar?${new URLSearchParams(query).toString()}`);
  return response.data;
}

/**
 * Convenience endpoint for the SERVICE_ONLY admin form's inline schedule
 * tab. Backed by `GET /service/:id/schedule` which returns the one ACTIVE
 * service-level schedule (or null when the operator hasn't configured one
 * yet). Companion to `PATCH /service/:id/schedule` (`upsertServiceSchedule`)
 * which create-or-updates based on existence.
 */
export async function getServiceSchedule(serviceId: string) {
  const response = await axiosInstance.get(`/service/${serviceId}/schedule`);
  return response.data;
}



// category 
export async function getCategory({ businessId }: { businessId: string }) {
  const response = await axiosInstance.get(`/category?businessId=${businessId}`);
  return response.data;
}

export async function createCategory({ body }: { body: any }) {
  const response = await axiosInstance.post(`/category`, body)
  return response.data 
}

export async function updateCategory({ id, body }: { id: string; body: any }) {
  const response = await axiosInstance.patch(`/category/${id}`, body)
  return response.data 
}

export async function deleteCategory({ id }: { id: string }) {
  const response = await axiosInstance.delete(`/category/${id}`)
  return response.data 
}

// branch
/**
 * Admin branch list. Backend always returns the paginated envelope
 * `{ items, total, page, limit, hasMore }`; we extract `items` here so the
 * page component receives a flat array. Admin omits `status` so it sees
 * both ACTIVE and INACTIVE rows; the customer-facing client passes
 * `status=ACTIVE`.
 */
export async function getBranch({
  businessId,
  status,
}: {
  businessId?: string;
  status?: 'ACTIVE' | 'INACTIVE';
} = {}) {
  const params = new URLSearchParams();
  if (businessId) params.append('businessId', businessId);
  if (status) params.append('status', status);
  const response = await axiosInstance.get(`/branch?${params.toString()}`);
  const data = response.data;
  return Array.isArray(data) ? data : (data?.items ?? data?.result ?? []);
}

export async function createBranch({ body }: { body: any }) {
  const config = body instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}
  const response = await axiosInstance.post(`/branch`, body, config)
  return response.data 
}

export async function updateBranch({id, body }: { id: string; body: any }) {
  const config = body instanceof FormData ? { headers: { 'Content-Type': undefined } } : {}
  const response = await axiosInstance.patch(`/branch/${id}`, body, config)
  return response.data 
}

export async function deleteBranch({ branchId }: { branchId?: string }) {
  const response = await axiosInstance.delete(`/branch/${branchId}`);
  return response.data;
}


/**
 * GET /business — server returns all businesses for SSO `admin`, otherwise only
 * businesses the user is a member of. For paginated admin CRUI, use {@link getBusinessAdminList}.
 */
export async function getAccessibleBusinesses() {
  const response = await axiosInstance.get(`/business`)
  return response.data
}

/** Admin only: paginated businesses with `channelCount` per row. */
export async function getBusinessAdminList(params: {
  page?: number
  limit?: number
}) {
  const response = await axiosInstance.get(`/business/admin/list`, {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    },
  })
  return response.data as {
    list: { id: string; name: string; status: string; channelCount: number; membershipCount: number }[]
    total: number
    page: number
    limit: number
  }
}

export type BusinessRoleDto = {
  id: string
  code: string
  name?: string | null
  permissions?: { codes?: string[] } | Record<string, unknown> | null
}

export type PermissionCatalogEntry = { code: string; label: string }

/** Domain-grouped catalog from the backend — keeps the 21 permission
 *  codes organized into 8 functional sections so the admin checklist
 *  is operator-comprehensible (vs a flat 21-row checklist). */
export type PermissionDomain = {
  key: string
  label: string
  codes: ReadonlyArray<PermissionCatalogEntry>
}

export async function listBusinessMemberships(businessId: string) {
  const response = await axiosInstance.get(`/business-membership`, {
    params: { businessId },
  })
  return response.data as {
    list: {
      id: string
      ssoUserId: string
      createdAt: string
      roleLinks?: { id: string; role: { id: string; code: string; name?: string | null } }[]
    }[]
  }
}

export async function createBusinessMembership(body: {
  businessId: string
  ssoUserId: string
  roleIds?: string[]
}) {
  const response = await axiosInstance.post(`/business-membership`, body)
  return response.data as { membership: any; message?: string }
}

export async function deleteBusinessMembership(id: string) {
  const response = await axiosInstance.delete(`/business-membership/${id}`)
  return response.data
}

export async function listBusinessRoles() {
  const response = await axiosInstance.get(`/business-role`)
  return response.data as { list: BusinessRoleDto[] }
}

export async function createBusinessRole(body: {
  code: string
  name?: string
  permissions?: Record<string, unknown>
}) {
  const response = await axiosInstance.post(`/business-role`, body)
  return response.data
}

export async function deleteBusinessRole(id: string) {
  const response = await axiosInstance.delete(`/business-role/${id}`)
  return response.data
}

export async function updateBusinessRole(
  id: string,
  body: { code?: string; name?: string; permissions?: Record<string, unknown> | null },
) {
  const response = await axiosInstance.patch(`/business-role/${id}`, body)
  return response.data as { role: BusinessRoleDto; message?: string }
}

export async function listPermissionCatalog() {
  const response = await axiosInstance.get(`/business-role/permissions/catalog`)
  return response.data as {
    catalog: PermissionCatalogEntry[]
    /** Domain-grouped shape (added 2026-06-17). Older backend builds
     *  return only `catalog` — caller should fall back to a single
     *  "All permissions" domain when `domains` is absent. */
    domains?: PermissionDomain[]
  }
}

// service сонгох
export async function getBusiness({ id }: { id: string }) {
  const response = await axiosInstance.get(`/business/${id}`)   
    return response.data
}

export async function updateBusiness({ id, body }: { id: string; body: any }) {
  const response = await axiosInstance.patch(`/business/${id}`, body);
  return response.data;
}

/**
 * RFC 7396 JSON Merge Patch over `business.settings`. `null` values delete
 * the corresponding key. Use this — not `updateBusiness` — for per-field
 * auto-save, so unrelated draft fields aren't clobbered.
 */
/** Preset cancellation rules with their customer sentence (404 on an older backend). */
export async function getCancellationPresets(): Promise<
  Array<{ name: "FLEXIBLE" | "MODERATE" | "STRICT" | "NO_REFUND"; tiers: Array<{ hoursBefore: number; refundPercent: number }>; sentence: string }>
> {
  const response = await axiosInstance.get(`/cancellation/presets`);
  return (response.data?.presets ?? []) as any;
}

export async function patchBusinessSettings(
  id: string,
  patch: Record<string, unknown>,
): Promise<{ settings: Record<string, unknown> }> {
  const response = await axiosInstance.patch(`/business/${id}/settings`, patch);
  return response.data as { settings: Record<string, unknown> };
}

/**
 * Upload a profile image (cover banner or square logo) for the business. Returns the
 * updated business row so the admin form picks up the persisted `coverImage` / `logoImage`
 * path immediately and can render a preview without a second fetch.
 */
export async function uploadBusinessImage({
  id,
  kind,
  file,
}: {
  id: string;
  kind: "cover" | "logo";
  file: File;
}) {
  const form = new FormData();
  form.append("image", file);
  form.append("kind", kind);
  const response = await axiosInstance.post(`/business/${id}/image`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

/** Payment-portal capabilities (proxied via booking backend). */
export async function getBusinessPaymentCapabilities(params: { id: string }) {
  const response = await axiosInstance.get(`/business/${params.id}/payment/capabilities`);
  return response.data as {
    code?: number;
    message?: string;
    result?: {
      providers?: string[];
      [providerId: string]: unknown;
    };
  };
}

export type PaymentBankAccount = {
  id: number;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  currency: string;
  iban?: string | null;
  isDefault?: boolean;
  /** payment-portal approval: online money goes only to APPROVED accounts. */
  approval_status?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  approval_reason?: string | null;
  test_passed_at?: string | null;
  /** `owner` = added by this business, `share` = shared with it. */
  via?: 'owner' | 'share';
};

/** This business's bank accounts in payment-portal (its own + shared), with approval status. */
export async function getBusinessPaymentBankAccounts(params: { id: string }) {
  const response = await axiosInstance.get(`/business/${params.id}/payment/bank-accounts`);
  return response.data as { list?: PaymentBankAccount[] };
}

export type BusinessOnboardingStep = {
  key: string;
  label: string;
  done: boolean;
  unknown?: boolean;
  ctaRoute?: string;
  hint?: string;
};

export type BusinessOnboardingChecklist = {
  businessId: string;
  allDone: boolean;
  steps: BusinessOnboardingStep[];
};

/** Per-business setup checklist for the Dashboard widget. */
export async function getBusinessOnboardingChecklist(params: { id: string }) {
  const response = await axiosInstance.get(
    `/business/${params.id}/onboarding-checklist`,
  );
  return response.data as BusinessOnboardingChecklist;
}

export type BusinessPaymentPortalHandoff = {
  url: string;
  expiresAt: string;
  integration: string;
  provider: string | null;
};

export async function getBusinessPaymentPortalHandoff(params: {
  id: string;
  provider?: string;
}): Promise<BusinessPaymentPortalHandoff> {
  const response = await axiosInstance.get(`/business/${params.id}/payment/portal-handoff`, {
    params: {
      provider: params.provider ?? "qpay",
    },
  });
  return response.data as BusinessPaymentPortalHandoff;
}

/**
 * Opens the portal's one-time `url` (a code bound to the signed-in owner / admin, ~2 minutes,
 * single use; the portal then asks them to sign in with SSO).
 */
export function openBusinessPaymentPortalHandoff(data: BusinessPaymentPortalHandoff): void {
  window.open(data.url, "_blank", "noopener,noreferrer");
}

export async function listUsers(params?: {
  page?: number;
  limit?: number;
  q?: string;
}) {
  const response = await axiosInstance.get(`/user`, {
    params: {
      page: params?.page ?? 1,
      limit: params?.limit ?? 20,
      ...(params?.q ? { q: params.q } : {}),
    },
  });
  return response.data as {
    list: Array<{
      id: string;
      name: string;
      email?: string | null;
      phone?: string | null;
      identities?: Array<{
        id: string;
        provider: string;
        providerUserId: string;
        tenantKey?: string | null;
      }>;
    }>;
    total: number;
    page: number;
    limit: number;
  };
}

export async function updateUser(params: {
  userId: string;
  body: {
    name?: string;
    email?: string;
    phone?: string;
  };
}) {
  const response = await axiosInstance.patch(`/user/${params.userId}`, params.body);
  return response.data as {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  };
}

export async function deleteUser({ userId }: { userId: string }): Promise<void> {
  await axiosInstance.delete(`/user/${userId}`);
}

/**
 * SSO-admin only. Consolidates two User rows into one — see
 * UserService.mergeUsers for the per-table data movement. Loser is
 * hard-deleted; profile data jsonb merges with winner's keys winning.
 */
export async function mergeUsers({
  winnerId,
  loserId,
}: {
  winnerId: string;
  loserId: string;
}): Promise<{
  mergedInto: string;
  mergedFrom: string;
  movedBookings: number;
  movedIdentities: number;
  droppedIdentities: number;
  mergedProfiles: number;
  movedProfiles: number;
}> {
  const res = await axiosInstance.post('/user/admin/merge', { winnerId, loserId });
  return res.data;
}

/**
 * SSO-admin only. Compact booking list for a user (across all
 * businesses), used to populate the split picker. Newest-first.
 */
export type UserBookingRow = {
  id: string;
  businessId: string | null;
  businessName: string | null;
  serviceId: string | null;
  serviceName: string | null;
  startTime: string | null;
  endTime: string | null;
  status: string;
  totalPaid: number | null;
};

export async function listBookingsForUser(userId: string): Promise<UserBookingRow[]> {
  const res = await axiosInstance.get(`/user/${userId}/bookings`);
  return res.data;
}

/**
 * SSO-admin only. Counterpart to mergeUsers: reassigns the supplied
 * `bookingIds` from `sourceUserId` onto a freshly-created User built
 * from `newUserContact`. Source survives with whatever didn't move.
 * See UserService.splitUser for the transaction.
 */
export async function splitUser(input: {
  sourceUserId: string;
  bookingIds: string[];
  newUserName?: string;
  newUserPhone?: string;
  newUserEmail?: string;
}): Promise<{
  sourceUserId: string;
  newUserId: string;
  movedBookings: number;
}> {
  const res = await axiosInstance.post('/user/admin/split', input);
  return res.data;
}

/**
 * SSO-admin only. Atomically clears `phone` from `fromUserId` and
 * assigns it to `toUserId` — the recycled-phone reassignment workflow.
 * See backend UserService.reassignPhone for the transaction shape.
 */
export async function reassignPhone(input: {
  fromUserId: string;
  toUserId: string;
  phone: string;
}): Promise<{
  fromUserId: string;
  toUserId: string;
  phone: string;
  phoneNormalized: string | null;
}> {
  const res = await axiosInstance.post('/user/admin/reassign-phone', input);
  return res.data;
}

export async function getServiceCategoryId({ categoryId, branchId }: { categoryId?: string; branchId?: string }) {
  const params = new URLSearchParams();
  if (categoryId) params.append('categoryId', categoryId);
  if (branchId) params.append('branchId', branchId);
  const queryString = params.toString();

  const response = await axiosInstance.get(`/service${queryString ? `?${queryString}` : ''}`)
  return response.data
}

// Option A
// Discriminated envelope returned by GET /booking/slots. Backend branches on
// `service.settings.scheduling.mode`:
//   - TIME_SLOTS (default): minute-level grid via `slots[]`.
//   - DAY_AVAILABILITY (FULL_DAY / FIXED_DAYS): per-day availability with
//     server-derived `startTime`/`endTime` so callers don't recompute tz math.
export type AdminTimeSlotsResponse = {
  mode: "TIME_SLOTS";
  timezone: string;
  /** `staffIds`: «Хэн ч болно» list only — free staff in the backend's pick order. */
  slots: Array<{ startTime: string; endTime: string; full?: LimitFull[]; staffIds?: string[] }>;
};
export type AdminDayAvailabilityResponse = {
  mode: "DAY_AVAILABILITY";
  timezone: string;
  scheduling: {
    mode: "FULL_DAY" | "FIXED_DAYS";
    durationUnit: "DAYS";
    durationValue: number;
    checkInTime?: string;
    checkOutTime?: string;
  };
  days: Array<{ date: string; available: boolean; derivedStart: string; derivedEnd: string; full?: LimitFull[] }>;
};
export type AdminSlotsResponse = AdminTimeSlotsResponse | AdminDayAvailabilityResponse;

export async function getSlots({ query }: { query: any }): Promise<AdminSlotsResponse> {
  const response = await axiosInstance.get(`/booking/slots?${new URLSearchParams(query).toString()}`);
  const raw = response.data as unknown;
  // Back-compat: legacy bare-array shape from older backends. Wrap as
  // TIME_SLOTS so callers don't need a third branch.
  if (Array.isArray(raw)) {
    return {
      mode: "TIME_SLOTS",
      timezone: "Asia/Ulaanbaatar",
      slots: raw as Array<{ startTime: string; endTime: string }>,
    };
  }
  const env = (raw ?? {}) as Record<string, unknown>;
  const tz =
    typeof env.timezone === "string" && env.timezone.trim()
      ? env.timezone
      : "Asia/Ulaanbaatar";
  if (env.mode === "DAY_AVAILABILITY" && Array.isArray(env.days)) {
    const sched = (env.scheduling ?? {}) as Record<string, unknown>;
    return {
      mode: "DAY_AVAILABILITY",
      timezone: tz,
      scheduling: {
        mode: sched.mode === "FIXED_DAYS" ? "FIXED_DAYS" : "FULL_DAY",
        durationUnit: "DAYS",
        durationValue: Number(sched.durationValue) || 1,
        checkInTime: typeof sched.checkInTime === "string" ? sched.checkInTime : undefined,
        checkOutTime: typeof sched.checkOutTime === "string" ? sched.checkOutTime : undefined,
      },
      days: env.days as Array<{ date: string; available: boolean; derivedStart: string; derivedEnd: string; full?: LimitFull[] }>,
    };
  }
  return {
    mode: "TIME_SLOTS",
    timezone: tz,
    slots: Array.isArray(env.slots)
      ? (env.slots as Array<{ startTime: string; endTime: string; full?: LimitFull[]; staffIds?: string[] }>)
      : [],
  };
}

// Option B
//TODO: staff-аар захиалдаг бол 
//FIXME: GET /staff?businessId={id} гэж асууж байгаад, тухайн staff-ийн availability-г GET /booking/slots?resourceId={staffId}&resourceType=staff&date={date}&serviceId={serviceId}&slotIntervalMinutes={slotIntervalMinutes} 
export async function getStaffBussiness({ id }: { id: string }) {
  const response = await axiosInstance.get(`/staff?businessId=${id}`)   
    return response.data
}

export async function linkUser({ body }: { body: any }) {
  const response = await axiosInstance.post(`/user/link`, body)
  return response.data
}


export async function getDashboardStats({ businessId, period }: { businessId: string; period?: string }) {
  const params = period ? `?period=${period}` : '';
  const response = await axiosInstance.get(`/booking/dashboard/${businessId}${params}`);
  return response.data;
}

export type BookingConsoleListRow = {
  id: string
  businessId: string
  businessName: string | null
  serviceId: string
  serviceName: string | null
  userId: string
  userName: string | null
  userPhone: string | null
  userEmail: string | null
  staffId: string | null
  staffName: string | null
  spaceId: string | null
  resourceId: string | null
  branchId: string | null
  branchName: string | null
  startTime: string
  endTime: string
  status: string
  totalPaid: number
  /** Booked with other services in one visit; absent on an older backend. */
  visitId?: string | null
  /** Paid by a payment-portal test mark (platform admins only); absent on an older backend. */
  isTest?: boolean
}

export async function listBookingsConsole(params: {
  page?: number
  limit?: number
  businessId?: string
  status?: string
  serviceId?: string
  from?: string
  to?: string
  resourceType?: 'staff' | 'space' | 'resource'
  resourceId?: string
  staffIds?: string[]
  spaceIds?: string[]
  resourceIds?: string[]
}) {
  const response = await axiosInstance.get(`/booking/admin/list`, {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      ...(params.businessId ? { businessId: params.businessId } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.serviceId ? { serviceId: params.serviceId } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
      ...(params.resourceType
        ? {
            resourceType: params.resourceType,
            ...(params.resourceId ? { resourceId: params.resourceId } : {}),
          }
        : {}),
      ...(params.staffIds?.length
        ? { staffIds: params.staffIds.join(',') }
        : {}),
      ...(params.spaceIds?.length
        ? { spaceIds: params.spaceIds.join(',') }
        : {}),
      ...(params.resourceIds?.length
        ? { resourceIds: params.resourceIds.join(',') }
        : {}),
    },
  })
  return response.data as {
    list: BookingConsoleListRow[]
    total: number
    page: number
    limit: number
  }
}

export async function listBookingsRangeConsole(params: {
  from: string
  to: string
  businessId?: string
  status?: string
  resourceType?: 'staff' | 'space' | 'resource'
  resourceId?: string
  staffIds?: string[]
  spaceIds?: string[]
  resourceIds?: string[]
  max?: number
}) {
  const response = await axiosInstance.get(`/booking/admin/range`, {
    params: {
      from: params.from,
      to: params.to,
      ...(params.max ? { max: params.max } : {}),
      ...(params.businessId ? { businessId: params.businessId } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.resourceType
        ? {
            resourceType: params.resourceType,
            ...(params.resourceId ? { resourceId: params.resourceId } : {}),
          }
        : {}),
      ...(params.staffIds?.length
        ? { staffIds: params.staffIds.join(',') }
        : {}),
      ...(params.spaceIds?.length
        ? { spaceIds: params.spaceIds.join(',') }
        : {}),
      ...(params.resourceIds?.length
        ? { resourceIds: params.resourceIds.join(',') }
        : {}),
    },
  })
  return response.data as {
    list: BookingConsoleListRow[]
    maxRows: number
    truncated: boolean
  }
}

export async function getBookingConsoleDetail(bookingId: string) {
  const response = await axiosInstance.get(`/booking/admin/${bookingId}`)
  return response.data as {
    booking: Record<string, unknown> & {
      id?: string
      businessId?: string
      startTime?: string
      endTime?: string
      status?: string
      totalPaid?: number
      settings?: Record<string, unknown> | null
      business?: { name?: string }
      service?: {
        name?: string
        duration?: number
        settings?: {
          bookingMode?: 'RESOURCE_BASED' | 'SERVICE_ONLY'
          scheduling?: {
            mode?: 'TIME_SLOTS' | 'FULL_DAY' | 'FIXED_DAYS'
            durationUnit?: 'MINUTES' | 'DAYS'
            durationValue?: number
            checkInTime?: string
            checkOutTime?: string
          } | null
        } | null
      }
      userId?: string | null
      user?: { name?: string; phone?: string | null; email?: string | null } | null
      guestName?: string | null
      guestPhone?: string | null
      guestEmail?: string | null
      staff?: { id?: string; firstName?: string | null; lastName?: string | null }
      space?: { id?: string; name?: string }
      resource?: { id?: string; name?: string }
    }
    payments: Array<{
      id: string
      amount: number
      type: string
      currency: string
      channel?: string | null
      createdAt?: string
    }>
  }
}

export async function cancelBookingConsole(params: {
  bookingId: string
  reason: string
  /** Money to give back — recorded as "refund owed" until staff settle it. */
  refundAmount?: number
}) {
  const response = await axiosInstance.patch(
    `/booking/admin/${params.bookingId}/cancel`,
    {
      reason: params.reason,
      ...(params.refundAmount ? { refundAmount: params.refundAmount } : {}),
    },
  )
  return response.data as { id?: string; status?: string; settings?: Record<string, unknown> }
}

export async function updateBookingConsole(params: {
  bookingId: string
  userName?: string
  userPhone?: string
  userEmail?: string
  startTime?: string
  operatorNote?: string
}) {
  const response = await axiosInstance.patch(`/booking/admin/${params.bookingId}`, {
    ...(params.userName != null ? { userName: params.userName } : {}),
    ...(params.userPhone != null ? { userPhone: params.userPhone } : {}),
    ...(params.userEmail != null ? { userEmail: params.userEmail } : {}),
    ...(params.startTime ? { startTime: params.startTime } : {}),
    ...(params.operatorNote !== undefined ? { operatorNote: params.operatorNote } : {}),
  })
  return response.data as Record<string, unknown>
}

/** OIDC console: create booking (walk-in allowed with empty contact → backend uses "Зочин"). */
/** Front desk: several services back to back in one visit. 404 = an older backend (no visits). */
export async function createVisitConsole(body: Record<string, unknown>) {
  const response = await axiosInstance.post(`/booking/admin/visits`, body)
  return response.data as { visitId: string; bookings: Array<{ id: string }>; warnings: string[][] }
}

/** «Бүх айлчлалыг зөөх»: every open line of the visit moves so line 1 starts at `startTime`. */
export async function moveVisitConsole(visitId: string, startTime: string) {
  const response = await axiosInstance.post(`/booking/admin/visits/${visitId}/move`, { startTime })
  return response.data as { bookings: unknown[]; warnings: string[][] }
}

export async function createBookingConsole(body: Record<string, unknown>) {
  const response = await axiosInstance.post(`/booking/admin`, body)
  return response.data as { id?: string; settings?: Record<string, unknown> | null }
}

/** Admin-only: maps external channel identities to a business*/
export async function listBusinessChannels(businessId: string) {
  const response = await axiosInstance.get(`/business-channel`, {
    params: { businessId },
  });
  return response.data;
}

export async function createBusinessChannel(body: {
  businessId: string;
  channel: string;
  externalKey: string;
  expireAt?: string;
  metadata?: Record<string, unknown>;
}) {
  const response = await axiosInstance.post(`/business-channel`, body);
  return response.data;
}

export async function deleteBusinessChannel(id: string) {
  const response = await axiosInstance.delete(`/business-channel/${id}`);
  return response.data;
}
